/**
 * Page mobile chauffeur : "Mon suivi GPS"
 * - Écran de consentement RGPD (signé + horodaté)
 * - Activation du tracking 5 min (en arrière-plan tant que l'onglet est ouvert)
 * - Wake Lock pour empêcher la mise en veille de l'écran
 * - Service Worker pour Periodic Background Sync (Android Chrome PWA)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  MapPin, Shield, Smartphone, AlertTriangle, CheckCircle, Loader2,
  Battery, Navigation, Power, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TEXTE_CONSENTEMENT_VERSION = 'v1.0-2026-02';
const TEXTE_CONSENTEMENT = `Je consens librement et en toute connaissance de cause au partage de ma position GPS avec mon employeur (ETA Sauvayre) pendant mes heures de travail, dans le but d'optimiser la planification des chantiers, vérifier les déplacements pour la facturation aux clients, et garantir ma sécurité en cas d'accident.

Conformément à l'article L1121-1 du Code du travail et au RGPD :
• Je peux retirer ce consentement à tout moment depuis cette page.
• Les données sont conservées 12 mois maximum, puis supprimées automatiquement.
• Le tracking ne fonctionne que tant que l'onglet est ouvert (ou la PWA active).
• Je peux consulter / supprimer mes données via mon employeur.`;

export default function MonSuiviGPSPage() {
  const [step, setStep] = useState('loading'); // loading | consent | active | error
  const [hasConsented, setHasConsented] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [lastPing, setLastPing] = useState(null);
  const [pingCount, setPingCount] = useState(0);
  const [position, setPosition] = useState(null);
  const [permission, setPermission] = useState('prompt');
  const [error, setError] = useState(null);
  const [battery, setBattery] = useState(null);
  const wakeLockRef = useRef(null);
  const intervalRef = useRef(null);
  const watchRef = useRef(null);

  // ---------- Consentement initial ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/geoloc/consent');
        setHasConsented(!!res.data?.has_consented);
        setStep(res.data?.has_consented ? 'active' : 'consent');
      } catch (e) {
        setStep('consent');
      }
    })();
  }, []);

  // ---------- Permission navigateur ----------
  const checkPermission = useCallback(async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermission(result.state);
      } catch (_) {}
    }
  }, []);
  useEffect(() => { checkPermission(); }, [checkPermission]);

  // ---------- Battery ----------
  useEffect(() => {
    if ('getBattery' in navigator) {
      navigator.getBattery().then((b) => {
        const update = () => setBattery(Math.round(b.level * 100));
        update();
        b.addEventListener('levelchange', update);
      }).catch(() => {});
    }
  }, []);

  // ---------- Service Worker ----------
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/geoloc-sw.js').then(async (reg) => {
        // Tente de s'inscrire au Periodic Background Sync (Android Chrome PWA)
        try {
          if ('periodicSync' in reg) {
            const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
            if (status.state === 'granted') {
              await reg.periodicSync.register('geoloc-sync', { minInterval: 5 * 60 * 1000 });
            }
          }
        } catch (_) {}
      }).catch(() => {});

      // Si le SW demande un ping (background sync)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'GEOLOC_REQUEST_PING') {
          sendPingNow(true);
        }
      });
    }
  }, []);

  // ---------- Wake Lock ----------
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      }
    } catch (e) {
      console.warn('Wake Lock indisponible', e);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (_) {}
  }, []);

  // Re-acquérir le Wake Lock quand on revient sur l'onglet
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && tracking && !wakeLockRef.current) {
        acquireWakeLock();
        sendPingNow(false); // ping immédiat au retour
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [tracking, acquireWakeLock]);

  // ---------- Geolocation helpers ----------
  const getCurrentPosition = () => new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Géolocalisation non supportée par ce navigateur'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000,
    });
  });

  const sendPingNow = useCallback(async (isBackground = false) => {
    try {
      const pos = await getCurrentPosition();
      const payload = {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy || null,
        altitude: pos.coords.altitude || null,
        heading: pos.coords.heading || null,
        speed: pos.coords.speed != null ? pos.coords.speed * 3.6 : null, // km/h
        battery: battery,
        is_background: isBackground || document.visibilityState !== 'visible',
      };
      await api.post('/geoloc/ping', payload);
      setPosition({ lat: payload.lat, lon: payload.lon, accuracy: payload.accuracy, speed: payload.speed });
      setLastPing(new Date());
      setPingCount((c) => c + 1);
      setError(null);
    } catch (e) {
      console.error('Ping error', e);
      const msg = e?.code === 1 ? 'Permission refusée'
                : e?.code === 2 ? 'Position indisponible'
                : e?.code === 3 ? 'Délai dépassé'
                : (e?.response?.data?.detail || e?.message || 'Erreur ping');
      setError(msg);
    }
  }, [battery]);

  // ---------- Démarrer / arrêter le tracking ----------
  const startTracking = useCallback(async () => {
    setError(null);
    try {
      const pos = await getCurrentPosition();
      setPermission('granted');
      // Premier ping immédiat
      await sendPingNow(false);
      setTracking(true);
      // Acquérir Wake Lock
      acquireWakeLock();
      // Watch position pour l'affichage live
      watchRef.current = navigator.geolocation.watchPosition((p) => {
        setPosition({
          lat: p.coords.latitude,
          lon: p.coords.longitude,
          accuracy: p.coords.accuracy,
          speed: p.coords.speed != null ? p.coords.speed * 3.6 : null,
        });
      }, () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
      // Ping toutes les 5 minutes
      intervalRef.current = setInterval(() => sendPingNow(false), 5 * 60 * 1000);
      toast.success('Suivi GPS activé (toutes les 5 min)');
    } catch (e) {
      const msg = e?.code === 1 ? 'Permission de géolocalisation refusée. Activez-la dans les paramètres du navigateur.'
                : (e?.message || 'Impossible de démarrer le suivi');
      setError(msg);
      if (e?.code === 1) setPermission('denied');
      toast.error(msg);
    }
  }, [sendPingNow, acquireWakeLock]);

  const stopTracking = useCallback(async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    await releaseWakeLock();
    setTracking(false);
    toast.info('Suivi GPS désactivé');
  }, [releaseWakeLock]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // ---------- Submit consentement ----------
  const submitConsent = async (accept) => {
    setConsentLoading(true);
    try {
      await api.post('/geoloc/consent', {
        consent: accept,
        text_consent: `${TEXTE_CONSENTEMENT_VERSION}\n\n${TEXTE_CONSENTEMENT}`,
        user_agent: navigator.userAgent,
      });
      setHasConsented(accept);
      setStep(accept ? 'active' : 'consent');
      toast.success(accept ? 'Consentement enregistré' : 'Consentement révoqué');
      if (!accept && tracking) await stopTracking();
    } catch (e) {
      toast.error('Erreur enregistrement consentement');
    } finally {
      setConsentLoading(false);
    }
  };

  // ---------- UI ----------
  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (step === 'consent' || !hasConsented) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="consent-screen">
        <div className="max-w-2xl mx-auto space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Shield className="w-6 h-6 text-primary" />
                Consentement de géolocalisation
              </CardTitle>
              <CardDescription>
                Avant d'activer le suivi GPS, veuillez accepter ce consentement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg whitespace-pre-line text-sm leading-relaxed">
                {TEXTE_CONSENTEMENT}
              </div>

              <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <input
                  type="checkbox"
                  id="consent-check"
                  className="mt-1 w-5 h-5"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent-check" className="text-sm">
                  J'ai lu et j'accepte la collecte et le traitement de ma position GPS dans les conditions ci-dessus.
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => submitConsent(true)}
                  disabled={!consentChecked || consentLoading}
                  className="flex-1"
                  data-testid="btn-accepter-consentement"
                >
                  {consentLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  J'accepte
                </Button>
                <Button
                  variant="outline"
                  onClick={() => submitConsent(false)}
                  disabled={consentLoading}
                  data-testid="btn-refuser-consentement"
                >
                  Je refuse
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // step === 'active'
  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="gps-active-screen">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Navigation className={`w-6 h-6 ${tracking ? 'text-green-500 animate-pulse' : 'text-muted-foreground'}`} />
                Mon suivi GPS
              </span>
              {tracking ? (
                <Badge className="bg-green-500">ACTIF</Badge>
              ) : (
                <Badge variant="outline">INACTIF</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Votre position est envoyée toutes les 5 minutes à votre employeur.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Tracking GPS</p>
                <p className="text-sm text-muted-foreground">
                  {tracking ? 'En cours - gardez l\'onglet ouvert' : 'Inactif'}
                </p>
              </div>
              <Switch
                checked={tracking}
                onCheckedChange={(v) => v ? startTracking() : stopTracking()}
                disabled={permission === 'denied'}
                data-testid="switch-tracking"
              />
            </div>

            {permission === 'denied' && (
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  Géolocalisation refusée. Activez-la dans les paramètres de votre navigateur, puis rechargez la page.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {position && (
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">Position</p>
                  <p className="font-mono text-xs">
                    {position.lat.toFixed(5)}, {position.lon.toFixed(5)}
                  </p>
                  {position.accuracy && (
                    <p className="text-xs text-muted-foreground">±{Math.round(position.accuracy)} m</p>
                  )}
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">Vitesse</p>
                  <p className="font-bold text-lg">
                    {position.speed != null ? `${Math.round(position.speed)} km/h` : '—'}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 border rounded">
                <p className="text-xs text-muted-foreground">Pings envoyés</p>
                <p className="font-bold">{pingCount}</p>
              </div>
              <div className="p-2 border rounded">
                <p className="text-xs text-muted-foreground">Dernier ping</p>
                <p className="font-bold text-xs">
                  {lastPing ? lastPing.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                </p>
              </div>
              <div className="p-2 border rounded">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <Battery className="w-3 h-3" /> Batterie
                </p>
                <p className="font-bold">{battery != null ? `${battery}%` : '—'}</p>
              </div>
            </div>

            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs">
                <strong>Astuce :</strong> Sur iPhone, gardez l'onglet ouvert dans Safari. Sur Android Chrome, installez l'app via "Ajouter à l'écran d'accueil" pour un meilleur fonctionnement en arrière-plan.
              </AlertDescription>
            </Alert>

            {tracking && (
              <Button
                onClick={() => sendPingNow(false)}
                variant="outline"
                className="w-full"
                data-testid="btn-ping-manuel"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Envoyer ma position maintenant
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Power className="w-4 h-4" /> Retirer mon consentement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => submitConsent(false)}
              disabled={consentLoading}
              className="w-full"
              data-testid="btn-revoquer-consentement"
            >
              Révoquer mon consentement RGPD
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Le tracking s'arrêtera immédiatement. Vous pourrez le réactiver à tout moment.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
